/**
 * useAuthElapsedProgress — sub-agent NO-ABORT (2026-08-13).
 *
 * Ticks an elapsedMs counter while a load-bearing auth flow (signup,
 * signin, password reset, purchase) is in flight. Used by the loading
 * cards to shift copy at 4s / 15s / 45s / 90s thresholds so the wait
 * feels intentional, not stuck. Design rules (Ean's articulation):
 *
 *   Best:   fast completion (2-3s)
 *   Fine:   slow completion + "still working…" cue (5-30s)
 *   Bad:    fast client abort before server completes
 *   Worst:  indefinite spin with no signal
 *
 * The hook itself does NOT abort — that's the caller's responsibility
 * (SIGNIN_TIMEOUT_MS + SIGNUP_TIMEOUT_MS in lib/api/auth.ts). This hook
 * just tells the UI *how long the request has been in flight* so it can
 * render the right "still working" copy.
 *
 * Usage:
 *
 *   const elapsedMs = useAuthElapsedProgress(submitting);
 *   const copy = pickAuthProgressCopy('signup', elapsedMs);
 *
 * When `active` is true, elapsedMs increments roughly every 500ms via
 * setInterval. When `active` flips to false, elapsedMs resets to 0 and
 * the interval is cleared.
 */

import { useEffect, useState } from 'react';

/** Tick interval for the elapsedMs counter — 500ms is plenty for copy shifts. */
const TICK_MS = 500;

export function useAuthElapsedProgress(active: boolean): number {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!active) {
      setElapsedMs(0);
      return undefined;
    }
    const startedAt = Date.now();
    setElapsedMs(0);
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, TICK_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [active]);

  return elapsedMs;
}

/**
 * Auth flow variants. Copy tuned per flow so "creating your account"
 * doesn't accidentally show up on a signin loading card. Extend when
 * adding new load-bearing flows (currently: signup, signin).
 */
export type AuthFlow = 'signup' | 'signin';

/**
 * Progressive copy shifts. Thresholds:
 *
 *   0 - 4s      normal spinner + intent copy
 *   4 - 15s     "still working…" reassurance
 *   15 - 45s    "almost there…" softer patience nudge
 *   45 - 90s    "taking longer than usual" strong wait cue
 *   >= 90s      recovery UI kicks in via the SIGNIN/SIGNUP_TIMEOUT_MS
 *               AbortController belt — this hook stops caring past that
 *               threshold because the caller has already thrown.
 *
 * Returns a pair — `title` for the AuthShell header, `subtitle` for the
 * "muted" body line underneath the spinner. Copy is Lori-grade: plain
 * language, no dev-speak, no false urgency.
 */
export function pickAuthProgressCopy(
  flow: AuthFlow,
  elapsedMs: number,
): { title: string; subtitle: string } {
  const intent = flow === 'signup' ? 'Creating your account' : 'Signing you in';
  const subject = flow === 'signup' ? 'workspace' : 'workspace';

  if (elapsedMs < 4_000) {
    return {
      title: `${intent}…`,
      subtitle:
        flow === 'signup'
          ? `Setting up your ${subject}.`
          : `Bringing you to your ${subject}.`,
    };
  }
  if (elapsedMs < 15_000) {
    return {
      title: `${intent}…`,
      subtitle: "Still working — first-time setups can take a moment.",
    };
  }
  if (elapsedMs < 45_000) {
    return {
      title: `${intent}…`,
      subtitle: "Almost there — thanks for your patience.",
    };
  }
  return {
    title: `${intent}…`,
    subtitle:
      "This is taking longer than usual. Please don't refresh yet — we're still working on it.",
  };
}
